import { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
  Box,
  Dialog,
  Typography,
  Button,
  FormControl,
  TextField,
  Switch,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import DateWithClear from "Components/UI/Form/DateWithClear";
import { directPayment, getInvoiceByInvoiceID } from "API/swesum";
import { usePopupNotificationContext } from "Context/NotificationPopup";

const style = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  gap: 4,
  padding: 4,
  width: "100&",
  height: "100%",
};

export default function DirectPaymentModal({
  invoice,
  open,
  service,
  setInvoice,
  onClose,
}) {
  const [acceptedByClient, setAcceptedByClient] = useState(false);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [remainingAmount, setRemainingAmount] = useState(
    invoice?.RemainingAmount
  );
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);

  const { openSuccess, openError } = usePopupNotificationContext();

  const handleDirectPayment = async () => {
    try {
      setLoading(true);
      const res = await directPayment(invoice.Id, {
        description: description,
        amount: remainingAmount,
        date: dayjs(selectedDate).format("YYYY-MM-DD"),
      });

      if (res) {
        const updatedInvoice = await getInvoiceByInvoiceID(invoice?.Id);
        setInvoice(updatedInvoice);
        openSuccess("Direktbetalning bokförd.");
      }
      return res;
    } catch (e) {
      openError(e.response.data);
    } finally {
      setLoading(false);
      onClose();
    }
  };

  useEffect(() => {
    setAcceptedByClient(false);
    setSelectedDate(new Date().toLocaleDateString());
    setRemainingAmount(invoice?.RemainingAmount);
  }, [open]);

  return (
    <>
      <Dialog open={open}>
        <Box sx={style}>
          <Typography variant="h5" fontWeight={"bold"}>
            Vill du boka fakturan som direktbetald?
          </Typography>
          <Box
            display={"flex"}
            flexDirection={"column"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            width={"100%"}
          >
            <FormControl fullWidth>
              <Box display="flex" gap={3} flexDirection="column">
                <Box display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={"medium"} fontSize={16} width={"50%"}>
                    Fakturanummer:
                  </Typography>
                  <Typography fontSize={16} width={"50%"}>
                    {invoice?.ExternalInvoiceNo}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={"medium"} fontSize={16} width={"50%"}>
                    Fakturatjänst:
                  </Typography>
                  <Typography fontSize={16} width={"50%"}>
                    {service}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={"medium"} fontSize={16} width={"50%"}>
                    Ursprungligt belopp:
                  </Typography>
                  <Typography fontSize={16} width={"50%"}>
                    {invoice?.Price}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0}>
                  <Typography fontWeight={"medium"} fontSize={16} width={"50%"}>
                    Återstående belopp:
                  </Typography>
                  <Typography fontSize={16} width={"50%"}>
                    {invoice?.RemainingAmount}
                  </Typography>
                </Box>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent={"flex-start"}
                  flexDirection="row"
                  gap={2}
                >
                  <Typography fontWeight={"medium"} fontSize={16}>
                    Godkänd av klient
                  </Typography>
                  <Switch
                    onChange={(event) =>
                      setAcceptedByClient(event.target.checked)
                    }
                  />
                </Box>
                {acceptedByClient ? (
                  <>
                    <Box display="flex" alignItems="center" gap={0}>
                      <Typography
                        fontWeight={"medium"}
                        fontSize={16}
                        width={"50%"}
                      >
                        Belopp:
                      </Typography>
                      <TextField
                        label={"Belopp"}
                        value={remainingAmount}
                        onChange={(e) => setRemainingAmount(e.target.value)}
                      />
                    </Box>
                    <Box display="flex" alignItems="center" gap={0}>
                      <Typography
                        fontWeight={"medium"}
                        fontSize={16}
                        width={"50%"}
                      >
                        Datum
                      </Typography>
                      <DateWithClear
                        onChange={(value) => setSelectedDate(value)}
                        value={dayjs(selectedDate)}
                      />
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="column"
                      gap={2}
                    >
                      <Typography
                        fontWeight={"medium"}
                        fontSize={16}
                        width={"100%"}
                      >
                        Notering:
                      </Typography>
                      <TextField
                        minRows={4}
                        placeholder="Notering"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        multiline
                        style={{
                          minWidth: 450,
                          width: "100%",
                          borderRadius: 3,
                          border: "1px solid gray",
                        }}
                      />{" "}
                    </Box>
                  </>
                ) : null}
              </Box>
            </FormControl>
          </Box>
          <Box
            display={"flex"}
            flexDirection={"row"}
            gap={5}
            justifyContent={"space-around"}
            height={55}
            width={"100%"}
          >
            <Button
              sx={{
                backgroundColor: "transparent",
                outline: "1px solid #1976d2",
                color: "#1976d2",
                width: "100%",
              }}
              onClick={() => onClose()}
            >
              Avbryt
            </Button>
            <LoadingButton
              variant="contained"
              onClick={handleDirectPayment}
              loading={loading}
              disabled={!acceptedByClient}
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                width: "100%",
              }}
            >
              Bokför
            </LoadingButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
}
